import React from "react";
import { toast } from "react-toastify";

interface CopyValueProps {
    value: string;
    /** What is being copied, for the tooltip — "phone number", "email". */
    label: string;
    className?: string;
}

/**
 * A value people copy rather than click through, inside a list row: the text
 * plus a clipboard icon. `data-no-row-nav` keeps selecting or copying it from
 * opening the row's record (see Table's row-click guard).
 */
const CopyValue: React.FC<CopyValueProps> = ({ value, label, className }) => {
    const copy = async (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation();
        try {
            await navigator.clipboard.writeText(value);
            toast.success("Copied to clipboard");
        } catch {
            // Clipboard API needs a secure context — say so instead of failing silently.
            toast.error("Could not copy — select the text and press Ctrl+C");
        }
    };

    return (
        <span className={`copy-value ${className ?? ""}`} data-no-row-nav>
            <span className="copy-value-text" title={value}>{value}</span>
            <i
                className="bi bi-clipboard copy-value-icon"
                role="button"
                tabIndex={0}
                title={`Copy ${label}`}
                aria-label={`Copy ${label} ${value}`}
                onClick={copy}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        copy(event);
                    }
                }}
            />
        </span>
    );
};

export default CopyValue;
